'use client';

import React, { useEffect, useState } from 'react';

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { cn } from '~/core/generic/shadcn-utils';
import { buttonVariants } from '~/core/ui/Button';
import {
  DateRange,
  DayFlag,
  DayPicker,
  SelectionState,
  TZDate,
  UI,
} from 'react-day-picker';

export type CalendarProps = Omit<
  React.ComponentProps<typeof DayPicker>,
  'onDayClick' | 'selected' | 'mode' | 'required'
> & {
  timeZone?: string;
  onDayClick?: (day: Date, modifiers: any, e: React.MouseEvent) => void;
  selected?: DateRange;
  /** This calendar component only supports 'range' mode internally. For single date selection, pass the same date for both from and to. */
  mode?: 'range' | 'single';
};

export const Calendar = ({
  className,
  classNames,
  showOutsideDays = false,
  timeZone,
  onDayClick,
  mode: _mode, // mode is accepted for API compatibility but internally always uses 'range'
  selected,
  ...props
}: CalendarProps) => {
  // Use the passed selected prop as the initial value for the internal state
  const [internalSelected, setInternalSelected] = useState<DateRange>(
    selected || { from: undefined },
  );

  useEffect(() => {
    // If selected is an empty object or undefined, clear the selection
    if (!selected || Object.keys(selected).length === 0) {
      setInternalSelected({ from: undefined });
    } else if (selected) {
      setInternalSelected(selected);
    }
  }, [selected]);

  const handleDayClick = (day: Date, modifiers: any, e: React.MouseEvent) => {
    const tzDate = new TZDate(day, timeZone);

    const newSelected: DateRange = { ...internalSelected };
    if (!newSelected.from || (newSelected.from && newSelected.to)) {
      newSelected.from = tzDate;
      newSelected.to = undefined;
    } else {
      if (tzDate < newSelected.from) {
        newSelected.to = newSelected.from;
        newSelected.from = tzDate;
      } else {
        newSelected.to = tzDate;
      }
    }
    setInternalSelected(newSelected);

    if (onDayClick) {
      onDayClick(tzDate, modifiers, e);
    }
  };

  return (
    <DayPicker
      mode="range"
      timeZone={timeZone}
      onDayClick={handleDayClick}
      selected={internalSelected}
      onSelect={() => {}}
      showOutsideDays={showOutsideDays}
      fixedWeeks
      className={cn('p-3', className)}
      classNames={{
        [UI.Months]: 'relative flex gap-4',
        [UI.Month]: 'space-y-4 mx-auto',
        [UI.MonthCaption]: 'flex justify-center items-center h-7 ',
        [UI.CaptionLabel]: 'text-lg font-bold',
        [UI.PreviousMonthButton]: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute left-1 top-0 h-7 w-7 bg-[#3369f440] p-0 opacity-50 hover:opacity-100',
        ),
        [UI.NextMonthButton]: cn(
          buttonVariants({ variant: 'outline' }),
          'absolute right-1 top-0 h-7 w-7 bg-[#3369f440] p-0 opacity-50 hover:opacity-100 ',
        ),
        [UI.MonthGrid]: 'w-full border-collapse space-y-1',
        [UI.Weekdays]: 'flex',
        [UI.Weekday]:
          'text-muted-foreground rounded-md w-9 font-semibold text-[0.8rem]',
        [UI.Week]: 'flex w-full mt-2 !bg-transparent',
        [UI.Day]:
          'h-9 w-9 text-center rounded-md text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        [UI.DayButton]: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-semibold aria-selected:opacity-100 hover:bg-primary hover:text-primary-foreground',
        ),
        [SelectionState.range_end]: 'day-range-end',
        [SelectionState.selected]:
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
        [SelectionState.range_middle]:
          'aria-selected:bg-accent aria-selected:text-accent-foreground  aria-selected:bg-[#3c83f620] ',
        [DayFlag.today]: '', // Remove the background and text color for today
        [DayFlag.outside]: 'day-outside  aria-selected:opacity-100',
        [DayFlag.disabled]: 'text-muted-foreground opacity-50',
        [DayFlag.hidden]: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ ...props }) => <Chevron {...props} />,
      }}
      {...props}
    />
  );
};

const Chevron = ({ orientation = 'left' }) => {
  switch (orientation) {
    case 'left':
      return (
        <ChevronLeftIcon className="h-4 w-4 !p-0 !py-0 !px-0  pointer-events-none" />
      );
    case 'right':
      return (
        <ChevronRightIcon className="h-4 w-4  !p-0 !py-0 !px-0  pointer-events-none" />
      );
    case 'up':
      return <ChevronUpIcon className="h-4 w-4  pointer-events-none" />;
    case 'down':
      return <ChevronDownIcon className="h-4 w-4  pointer-events-none" />;
    default:
      return null;
  }
};
